import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, Sparkles, Loader2, Lock } from 'lucide-react';
import { motion } from 'framer-motion';

// Stable refs outside component → no re-renders from framer-motion prop identity changes
const ANIM_INITIAL = { opacity: 0, y: 20 };
const ANIM_VIEW = { opacity: 1, y: 0 };
const ANIM_VIEWPORT = { once: true };

/** Single pricing tier card. Pure presentational. */
const PricingTierCard = ({ tier, index, t, lang, isLoading, disabled, onCta }) => {
  const ctaLabel = tier.plan === 'free'
    ? t('pricing.start.free')
    : (lang === 'fr' ? `Choisir ${t(tier.nameKey)}` : `Get ${t(tier.nameKey)}`);

  return (
    <motion.div
      initial={ANIM_INITIAL}
      whileInView={ANIM_VIEW}
      viewport={ANIM_VIEWPORT}
      transition={{ delay: index * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`relative p-8 lg:p-10 rounded-2xl border transition-all duration-300 ${
        tier.highlight
          ? 'border-[#D4FF00]/60 bg-gradient-to-br from-[#D4FF00]/[0.08] via-transparent to-transparent shadow-[0_0_60px_-15px_rgba(212,255,0,0.35)] lg:scale-[1.02] hover:shadow-[0_0_80px_-15px_rgba(212,255,0,0.55)]'
          : 'border-white/10 bg-white/[0.02] hover:border-white/20'
      }`}
      data-testid={`tier-${tier.plan}`}
    >
      {/* Popular badge — floats above the highlighted card */}
      {tier.highlight && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3.5 py-1 rounded-full bg-[#D4FF00] text-black text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-lg shadow-[#D4FF00]/20"
          data-testid={`tier-badge-popular-${tier.plan}`}
        >
          <Sparkles className="w-3 h-3" /> {t('pricing.popular')}
        </div>
      )}

      {/* Header */}
      <div className="mb-7">
        <h3 className="font-heading text-2xl font-bold tracking-tight">{t(tier.nameKey)}</h3>
        <p className="text-zinc-500 text-sm mt-1">{t(tier.subKey)}</p>
      </div>

      {/* Price */}
      <div className="mb-8">
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-6xl font-black tracking-tighter">${tier.price}</span>
          <span className="text-zinc-500 text-sm">{t('pricing.month')}</span>
        </div>
        {/* Founder pricing chip — honest, no fake strikethrough */}
        {tier.plan !== 'free' && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#D4FF00]/25 bg-[#D4FF00]/[0.06] text-[#D4FF00] text-[11px] font-semibold uppercase tracking-wider"
            data-testid={`tier-badge-founder-${tier.plan}`}
          >
            <Lock className="w-3 h-3" /> {t('pricing.founder')}
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-9">
        {tier.features.map(([k, ok]) => (
          <li key={k} className="flex items-start gap-3 text-sm leading-relaxed">
            {ok
              ? <Check className="w-4 h-4 text-[#D4FF00] shrink-0 mt-0.5" />
              : <X className="w-4 h-4 text-zinc-700 shrink-0 mt-0.5" />}
            <span className={ok ? 'text-zinc-200' : 'text-zinc-600 line-through'}>{t(k)}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Button
        onClick={onCta}
        disabled={isLoading || disabled}
        className={`w-full h-12 font-semibold text-sm rounded-xl transition-all duration-200 ${
          tier.highlight
            ? 'bg-[#D4FF00] text-black hover:bg-[#B3D600] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4FF00]/25 active:translate-y-0'
            : 'bg-white/5 text-white hover:bg-white/[0.08] border border-white/10 hover:border-white/20'
        }`}
        data-testid={`tier-cta-${tier.plan}`}
      >
        {isLoading
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{lang === 'fr' ? 'Redirection...' : 'Redirecting...'}</>
          : ctaLabel}
      </Button>
    </motion.div>
  );
};

export default PricingTierCard;
